RivalIQ.apps.Dashboard.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {

    'use strict';

    const MetricBar = widgets.MetricBar = RivalIQ.apps.Analyze.views.BarChart.extend({

        className: function () {
            const me = this;

            const barPatternsClass = (me.getProp('renderBarPatterns') ? 'bar-patterns' : null);
            return _.compact(['chart horizontal-bar block-chart text-center', barPatternsClass]).join(' ');
        },

        minHeight: RivalIQ.enums.chart.CHART_HEIGHT,
        staticLabels: true,
        dashboardModelEvents: {
            'change': 'reload'
        },

        getTimeAnalysisMode: function () {
            const me = this;
            return me.dashboardModel.metrics[0].timeAnalysisMode;
        },

        getFormatX: function () {
            const me = this;
            const metric = me.primaryMetric;
            return _.bind(metric.format, metric);
        },

        getPopoverFormatX: function () {
            const me = this;
            const metric = me.primaryMetric;
            return _.bind(metric.format, metric, _, {
                expand: true
            });
        },

        loadData: function () {
            const me = this;

            // If the metric has submetrics create a metric for the sum
            let sumMetric;
            if (RivalIQ.metric.getFreeDimensions(me.dashboardModel.metrics[0]).length > 1) {
                sumMetric = RivalIQ.metric.pin(me.dashboardModel.metrics[0], 1);
            }

            const defs = [
                me.dashboardModel.metrics[0]
            ];

            const primaryMetricIdx = 0;
            let sumMetricIdx;
            let secondaryMetricIdx;
            let deltaAbsoluteMetricIdx;
            let benchmarkMetricIdx;

            if (sumMetric) {
                sumMetricIdx = defs.length;
                defs.push(sumMetric);
            }

            if (me.dashboardModel.secondary) {
                secondaryMetricIdx = defs.length;
                defs.push(me.dashboardModel.secondary);
            }

            const collection = new RivalIQ.collections.MetricDefinitions(defs);
            const primaryMetric = collection.first();

            let deltaAbsoluteMetric;
            if (primaryMetric.get('timeAnalysisMode') === RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE) {
                deltaAbsoluteMetric = primaryMetric.build({
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE
                });

                deltaAbsoluteMetricIdx = collection.size();
                collection.add(deltaAbsoluteMetric);
            }

            if (me.getProp('showBenchmarkLine')) {
                const selectedBenchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(me.getProp('selectedBenchmarkCompareAgainst'));
                const benchmarkDimensions = _.difference(primaryMetric.get('dimensions') || [], primaryMetric.getFreeDimensions());

                let exclusionDimension = null;
                const primaryMetricFreeDimensions = primaryMetric.getFreeDimensions();
                if (primaryMetricFreeDimensions.length > 1) {
                    exclusionDimension = _.last(primaryMetricFreeDimensions);
                }

                const benchmarkMetric = primaryMetric.build({
                    format: undefined,
                    exclusionDimension: exclusionDimension,
                    dimensions: benchmarkDimensions,
                    aggregate: selectedBenchmarkCompareAgainst.aggregate,
                    benchmarkCompaniesFilter: selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter
                }, {
                    clobberDimensions: true
                });

                benchmarkMetricIdx = collection.size();
                collection.add(benchmarkMetric);
            }

            return collection.fetch().done(function (metrics) {

                me.primaryMetric = metrics.at(primaryMetricIdx);
                me.companyData = me.primaryMetric.companyData;

                if (deltaAbsoluteMetric) {
                    me.deltaAbsoluteMetric = metrics.at(deltaAbsoluteMetricIdx);
                } else {
                    me.deltaAbsoluteMetric = null;
                }

                if (sumMetric) {
                    me.sumMetric = metrics.at(sumMetricIdx);
                } else {
                    me.sumMetric = null;
                }

                if (me.dashboardModel.secondary) {
                    me.secondaryMetric = metrics.at(secondaryMetricIdx);
                } else {
                    me.secondaryMetric = null;
                }

                if (me.getProp('showBenchmarkLine')) {
                    me.benchmarkMetric = metrics.at(benchmarkMetricIdx);
                }
            });
        },

        hasSecondaryData: function () {
            const me = this;
            return Boolean(me.getProp('secondary') || me.secondaryMetric);
        },

        onShow: _.noop,

        onAttachLoaded: function () {
            const me = this;
            me.width = me.$el.width();
            RivalIQ.apps.Analyze.views.BarChart.prototype.onShow.call(me);
            if (me.data.length) {
                me.graph.onAttachLoaded();
            }
        },

        isContentLinkable: function () {
            const me = this;
            return !me.hasSubmetrics() && me.primaryMetric.hasRelatedActivity();
        },

        isVolumetric: function () {
            const me = this;
            return me.primaryMetric.getMetricType() === RivalIQ.enums.metricType.VOLUME;
        },

        hasSubmetrics: function () {
            const me = this;
            return me.primaryMetric.getFreeDimensions().length > 1;
        },

        getSubmetrics: function () {
            const me = this;
            const metric = me.primaryMetric;
            const dimension = _.last(metric.getFreeDimensions());

            return _.map(metric.getStaticDimensionValues(dimension), function (dimensionValue) {
                return {
                    metric: dimensionValue,
                    legendName: metric.formatDimension(dimension, dimensionValue),
                    class: metric.byDimension('className', dimensionValue, dimension)
                };
            });
        },

        getBarData: function (data, secondaryData) {
            const me = this;
            const primaryMetric = me.primaryMetric;
            const focusCompanyId = me.getProp('focusCompanyId');

            return _.map(data, function (data, i) {
                const id = Number(_.head(data));
                const noData = me.getEmptyDataMessage(id, []);

                const datum = {
                    id: id,
                    y: i,
                    value: _.last(data),
                    name: primaryMetric.getDimension(data, 0),
                    focus: id === focusCompanyId,
                    secondary: _.last(secondaryData[i]),
                    noDataMessage: noData.message,
                    noDataTitle: noData.title,
                    noDataDescription: noData.description,
                    noDataUnboldTitle: noData.unboldTitle,
                    hasChannel: me.hasChannel(id)
                };

                if (datum.value && _.isNull(datum.secondary)) {

                    const noHistoricData = me.secondaryMetric.getNoHistoricData();
                    const metricName = me.secondaryMetric.getName();
                    const timeAnalysisMode = me.secondaryMetric.getTimeAnalysisMode();
                    const source = me.secondaryMetric.get('source');
                    const company = RivalIQ.state.landscape.get('companies').get(id);

                    if (company && RivalIQ.enums.sourceDetails[source].supportsNotEnoughHistory) {
                        const companyName = _.escape(company.get('name'));
                        const dataStatus = RivalIQ.apps.Shell.utils.getDataFreshnessState();
                        const timePeriod = me.primaryMetric.getDataTimePeriod();
                        const earliestDataDateByCompanyId = me.primaryMetric.getEarliestDataDate();
                        const incompleteDataType = RivalIQ.utils.incompleteData.getIncompleteDataType(company, source, dataStatus.updateInFlight, timePeriod, earliestDataDateByCompanyId, noHistoricData, [], timeAnalysisMode);
                        const incompleteDataText = RivalIQ.utils.incompleteData.getIncompleteDataText(incompleteDataType, company.attributes, timePeriod, companyName, metricName, source, earliestDataDateByCompanyId, null, noHistoricData, [], timeAnalysisMode);
                        if (incompleteDataText) {
                            datum.showNullSecondaryWarning = true;
                            datum.partialDataWarning = {
                                popover: {
                                    content: RivalIQ.utils.incompleteData.incompleteDataPopoverTemplate({
                                        title: incompleteDataText.title,
                                        description: incompleteDataText.description
                                    })
                                }
                            };
                        }
                    }
                }

                return datum;
            });
        },

        getStackedBarData: function (barData, sumData, secondaryData) {
            const me = this;

            const primaryMetric = me.primaryMetric;
            const groupedData = _.groupBy(barData, _.head);
            const groupedSecondaryData = _.groupBy(secondaryData, _.head);
            const groupedSumData = _.groupBy(sumData, _.head);

            const focusCompanyId = me.getProp('focusCompanyId');

            const submetricExpectedOrder = _.reduce(me.getSubmetrics(), function (memo, submetric, idx) {
                memo[submetric.metric] = idx;
                return memo;
            }, {});

            return _.map(groupedData, function (data, i) {
                const id = Number(_.head(data[0]));
                const name = primaryMetric.getDimension(data[0], 0);
                const secondaryDatum = (groupedSecondaryData[id] ? _.last(groupedSecondaryData[id][0]) : undefined);
                const noData = me.getEmptyDataMessage(id, me.getSubmetrics());

                const datum = {
                    id: id,
                    y: i,
                    name: name,
                    noDataMessage: noData.message,
                    noDataTitle: noData.title,
                    noDataDescription: noData.description,
                    noDataUnboldTitle: noData.unboldTitle,
                    hasChannel: me.hasChannel(id),
                    focus: id === focusCompanyId,
                    secondary: secondaryDatum,
                    value: _.last(groupedSumData[id][0])
                };

                let calculationMetadata = primaryMetric.getCalculationMetadata(id);
                let partialDataMetric = primaryMetric;

                if (_.get(calculationMetadata, ['missingAllSources'])) {
                    datum.value = null;
                } else if (me.secondaryMetric) {
                    if (_.isEmpty(_.get(calculationMetadata, ['missingSources']))) {
                        calculationMetadata = me.secondaryMetric.getCalculationMetadata(id);
                        partialDataMetric = me.secondaryMetric;

                        datum.showNullSecondaryWarning = true;
                    }
                }

                if (!_.isEmpty(_.get(calculationMetadata, ['missingSources'])) && !calculationMetadata.missingAllSources) {
                    // Bar charts will only show the warning triangle if there's partial data, but some data (i.e., no warning triangle when there is no data).
                    datum.partialDataWarning = RivalIQ.utils.incompleteData.getIncompleteDataPopover('companyId', id, partialDataMetric, calculationMetadata);
                }

                // Reorder the submetrics so that the bar metrics are in the same order as the legend.
                data = _.sortBy(data, function (datum) {
                    return submetricExpectedOrder[datum[1]];
                });

                datum.submetrics = _.map(data, function (datum) {
                    const submetricValue = _.last(datum);

                    return {
                        id: id,
                        value: submetricValue,
                        class: primaryMetric.getDimension(datum, 1, 'className'),
                        companyName: name,
                        // NOTE this key is used for filtering exclusions
                        metric: datum[1],
                        tooltipName: primaryMetric.getDimension(datum, 1, 'format')
                    };
                });

                return datum;
            });
        },

        getData: function () {
            const me = this;

            const primaryMetric = me.primaryMetric;

            let data = primaryMetric.getChartData({
                guaranteeCompanyRow: true
            });
            let secondaryData = [];

            if (me.secondaryMetric) {
                secondaryData = me.secondaryMetric.getChartData();
            }

            if (me.sumMetric) {
                const sumData = me.sumMetric.getChartData();
                data = me.getStackedBarData(data, sumData, secondaryData);
            } else {
                data = me.getBarData(data, secondaryData);
            }

            data = data.sort(_.bind(me.dataSorter, me));

            const inclusions = me.getProp('inclusions');
            return me.elisionStrategy(data, me.renderedDataLimit, inclusions, me.getOption('staticLabels'));
        },

        initialize: function () {
            const me = this;
            RivalIQ.apps.Analyze.views.BarChart.prototype.initialize.apply(me, arguments);
        }
    });

    const MetricBarChartWidget = widgets.ChartWidget.extend({
        view: MetricBar,

        hasFooter: _.constant(true),
        footerView: App.views.WarningLegendFooterView,

        propValues: {
            timeAnalysisMode: [
                RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                RivalIQ.enums.timeAnalysisMode.COMPARE_PERIOD,
                RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE,
                RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
            ]
        },

        maxDimensionCount: function (metrics) {
            if (RivalIQ.metric.graph.isSummable(metrics[0])) {
                return 2;
            }

            return 1;
        },

        isSupported: function (props) {
            if (!props.metrics || props.metrics.length !== 1) {
                return false;
            }

            if (!_.every(props.metrics, RivalIQ.metric.property.isNumeric)) {
                return false;
            }

            return true;
        },

        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                useUniversalMetricsApi: me.curation.isCustomDashboard
            });

            me.metrics = me.buildMetrics(props);

            return props;
        },

        propsToOmitFromSerialization: ['inclusions'],

        viewMorePath: function () {
            const me = this;
            return me.getPath({
                inclusions: 3
            });
        },
        pptExportProps: function (props) {
            props.inclusions = 0;
            return props;
        },
        pdfExportProps: function (props) {
            props.inclusions = 0;
            return props;
        },
        csvExportProps: function () {
            const me = this;

            let sumMetric;
            if (RivalIQ.metric.getFreeDimensions(me.metrics[0]).length > 1) {
                sumMetric = RivalIQ.metric.pin(me.metrics[0], 1);
            }

            let metrics = me.metrics.concat(_.compact([sumMetric, me.secondary]));

            if (me.getProp('showBenchmarkLine')) {
                const selectedBenchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(me.getProp('selectedBenchmarkCompareAgainst'));
                metrics = metrics.concat(_.map(metrics, function (metric) {
                    const benchmarkDimensions = _.without(metric.dimensions || [], 'companyId');
                    return RivalIQ.metric.build(metric, {
                        extraFreeDimensions: {
                            companyId: selectedBenchmarkCompareAgainst.name
                        },
                        aggregate: selectedBenchmarkCompareAgainst.aggregate,
                        benchmarkCompaniesFilter: selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter,
                        dimensions: benchmarkDimensions,
                        useMatchingMetricColumn: true
                    }, {
                        clobberDimensions: true
                    });
                }));
            }

            const collection = new RivalIQ.collections.MetricDefinitions(metrics);

            return {
                type: 'metric',
                wideRow: true,
                nameDimensions: ['channel', 'timeAnalysisMode'],
                metric: collection.invoke('serialize')
            };
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.METRIC_BAR_CHART, MetricBarChartWidget);

    const MetricBarChangeChartWidget = widgets.MetricBarChangeChartWidget = MetricBarChartWidget.extend({
        getChartTitle: function () {
            const me = this;
            return RivalIQ.metric.names.metricName(me.metrics[0], {
                dimensions: ['channel']
            });
        },
        propValues: {
            timeAnalysisMode: [
                RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD
            ]
        },
        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                useUniversalMetricsApi: me.curation.isCustomDashboard
            });

            me.metrics = me.buildMetrics(props);
            const barMetric = props.metrics[0];

            // NOTE we don't actually want to specify a secondary metric in
            // props if it does not exist, because changing the metric in the
            // chart builder will not clear the prop, and it will then become
            // stale (i.e., it will be fixed to the previous measure). this
            // approach makes sure that any specified secondary metric will be
            // static, otherwise we will assume % change.
            let secondaryMetric = props.secondary;
            if (!secondaryMetric) {
                secondaryMetric = RivalIQ.metric.pin(RivalIQ.metric.build(barMetric, {
                    timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
                }), 1);
            }
            me.secondary = RivalIQ.metric.build(props, secondaryMetric);

            return props;
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.METRIC_BAR_CHART_WITH_CHANGE, MetricBarChangeChartWidget);

});
